import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HeroBackdrop from "@/components/HeroBackdrop";
import HeroActions from "@/components/HeroActions";
import { RESSOURCES } from "@/data/ressources";

export default function Ressources() {
  return (
    <div className="min-h-screen bg-[#FAF9F7] flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Header / Intro */}
        <section className="relative overflow-hidden bg-[#FAF9F7] grain">
          <HeroBackdrop variant="circulation" />
          <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pt-36 lg:pt-44 pb-16 lg:pb-20">
            <p
              data-testid="ressources-eyebrow"
              className="label-eyebrow text-[#666A70] mb-8"
            >
              Ressources
            </p>
            <h1
              data-testid="ressources-title"
              className="font-sans font-semibold text-[#181A1D] text-balance tracking-tight leading-[1.06] text-4xl md:text-5xl lg:text-[56px] max-w-4xl animate-fade-up"
            >
              Comprendre comment vos informations circulent.
            </h1>
            <div aria-hidden="true" data-testid="h1-marker" className="h1-marker mt-8 animate-fade-up" />
            <p
              data-testid="ressources-intro"
              className="mt-8 max-w-2xl text-base lg:text-lg leading-relaxed text-[#52565C] text-pretty animate-fade-up"
              style={{ animationDelay: "120ms" }}
            >
              Une progression en cinq temps : comprendre comment les
              données circulent, pourquoi l'open data et la publicité
              légale ne justifient pas toutes les rediffusions, leurs
              effets concrets sur les personnes, les leviers
              mobilisables et, en fil conducteur, la distinction entre
              donnée publique et exposition publique.
            </p>
            <div
              data-testid="ressources-links"
              className="mt-10"
            >
              <HeroActions />
            </div>
          </div>
          <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="h-px bg-[#E2DEDA]" />
          </div>
        </section>

        {/* Grid of resources */}
        <section className="bg-[#FAF9F7]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
            <ul
              data-testid="ressources-grid"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4"
            >
              {RESSOURCES.map((r, idx) => (
                <li
                  key={r.slug}
                  className="group bg-white rounded-lg border border-[#E7E3DE] overflow-hidden transition-[border-color,box-shadow,transform] duration-200 hover:border-[#B9B3AC] hover:shadow-[0_4px_20px_-8px_rgba(24,26,29,0.12)]"
                >
                  <Link
                    to={`/comprendre/${r.slug}`}
                    data-testid={`ressource-card-${r.slug}`}
                    className="block h-full focus:outline-none"
                  >
                    <div
                      data-testid={`vignette-${idx}`}
                      className="relative aspect-[16/7] w-full overflow-hidden border-b border-[#E2DEDA] bg-[#F2F0ED]"
                    >
                      <img
                        src={`/images/comprendre/${r.slug}.jpg`}
                        alt=""
                        loading={idx > 1 ? "lazy" : "eager"}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                      />
                      <span
                        aria-hidden="true"
                        className="absolute left-5 top-4 inline-flex items-center gap-2 bg-[#FAF9F7]/90 backdrop-blur-sm rounded-md px-2.5 py-1.5 font-mono text-[12px] font-medium tracking-[0.16em] text-[#181A1D] select-none"
                      >
                        <span className="inline-block w-3 h-[2px] bg-[#E94B3C]" />
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                    </div>

                    <div className="px-8 lg:px-9 py-9 lg:py-10">
                      <div className="mb-6">
                        <span className="font-mono text-[11px] font-medium tracking-[0.14em] uppercase text-[#5A5E64]">
                          {r.categorie}
                        </span>
                      </div>

                      <h2 className="font-sans font-semibold text-[#181A1D] text-[22px] lg:text-[25px] leading-snug tracking-tight mb-5 text-balance group-hover:text-[#B83A34] transition-colors duration-200">
                        {r.titre}
                      </h2>

                      <p className="text-[15px] leading-relaxed text-[#52565C] text-pretty">
                        {r.resume}
                      </p>

                      <div className="mt-8 pt-6 border-t border-[#EFEBE6] flex items-center justify-between gap-4">
                        <span className="text-[13px] font-medium text-[#181A1D] inline-flex items-center gap-2 underline decoration-[#D8D3CD] underline-offset-4 group-hover:decoration-[#E94B3C] transition-colors duration-200">
                          Lire
                          <ArrowUpRight
                            size={14}
                            strokeWidth={1.5}
                            className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                          />
                        </span>
                        {r.theme ? (
                          <span
                            data-testid={`ressource-theme-${r.slug}`}
                            className="font-mono text-[10px] tracking-[0.14em] uppercase text-[#7C8086] text-right"
                          >
                            {r.theme}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>

            <p
              data-testid="ressources-disclaimer"
              className="mt-16 max-w-2xl text-[13px] leading-relaxed text-[#666A70] italic font-normal"
            >
              Ces textes ont vocation à présenter, de manière sobre et
              documentée, les enjeux relatifs à l'exposition numérique
              professionnelle et patrimoniale. Ils n'engagent ni avis
              juridique individualisé ni recommandation particulière.
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
